// Every instant-video endpoint and response shape in one place
// (backend: docs/virtual-consultations/API.md, "Instant video").
import { callApi } from '../../utils/apiCall';

const base = (practiceId) => `/api/v1/practices/${practiceId}/instant-video/`;
const quiet = { suppressServerErrorDispatch: true };

// The API sends a boolean; older builds sent a head count.
const readStaffOnline = (raw) => {
    if (typeof raw === 'boolean') return raw;
    return Number.isFinite(raw) ? raw > 0 : null;
};

// GET/PUT/heartbeat all answer the same status body.
const readStatus = (data) => {
    if (!data || typeof data.enabled !== 'boolean') return null;
    return {
        enabled: data.enabled,
        inHours: data.in_hours !== false,
        staffOnline: readStaffOnline(data.staff_online),
        // Only an explicit false counts: older builds send no capacity.
        capacity: typeof data.capacity === 'boolean' ? data.capacity : null,
        available: data.available === true,
        // When the practice next opens ("9am tomorrow"); null when open, no hours set or unknown. Always
        // present so a fresher reply clears a stale value when merged.
        opensWhen: typeof data.opens_when === 'string' && data.opens_when ? data.opens_when : null,
        // Only an explicit true shows the switch. A reply without the field (heartbeat) leaves it untouched
        // when merged into the state, so it is omitted rather than defaulted.
        ...(typeof data.offered === 'boolean' ? { offered: data.offered } : {}),
    };
};

// { ok: true, ...status } | { ok: false, forbidden? } — a GET that fails hides the switch.
export async function getInstantVideoState(practiceId) {
    const res = await callApi(base(practiceId), 'GET', null, false, quiet);
    const status = res?.ok ? readStatus(res.data) : null;
    return status ? { ok: true, ...status } : { ok: false, forbidden: res?.status === 403 };
}

// A 403 means the user may look but not change: the switch turns read-only.
export async function setInstantVideoEnabled(practiceId, enabled) {
    const res = await callApi(base(practiceId), 'PUT', { enabled }, false, quiet);
    const status = res?.ok ? readStatus(res.data) : null;
    return status ? { ok: true, ...status } : { ok: false, forbidden: res?.status === 403 };
}

// Only managers count as online; anyone else's 403 is expected and ignored.
export async function sendInstantVideoHeartbeat(practiceId) {
    const res = await callApi(`${base(practiceId)}heartbeat/`, 'POST', {}, false, quiet);
    const status = res?.ok ? readStatus(res.data) : null;
    return status ? { ok: true, ...status } : { ok: false };
}

// What the card does for each way an accept can end.
export const ACCEPT_OUTCOMES = {
    taken: { message: 'Someone else took this call.', remove: true },
    no_capacity: { message: 'No diary is free for this call any more.', remove: true },
    gone: { message: 'This call has ended.', remove: true },
    // Includes no_treatment and any code a newer or older build may send.
    other: { message: "Couldn't accept this call.", remove: false },
};

// -> { ok: true, appointmentId, leadId } | { ok: false, outcome: keyof ACCEPT_OUTCOMES }
export async function acceptInstantVideoRequest(practiceId, requestId) {
    const res = await callApi(`${base(practiceId)}requests/${encodeURIComponent(requestId)}/accept/`, 'POST', {}, false, quiet);
    if (res?.ok) {
        if (res.data?.appointment_id == null) return { ok: false, outcome: 'other' };
        return { ok: true, appointmentId: res.data.appointment_id, leadId: res.data.lead_id };
    }
    const code = res?.data?.code;
    if (res?.status === 410) return { ok: false, outcome: 'gone' };
    if (res?.status === 409 && ['taken', 'no_capacity'].includes(code)) return { ok: false, outcome: code };
    // A bare 409 from an older build means someone got there first.
    if (res?.status === 409 && !code) return { ok: false, outcome: 'taken' };
    return { ok: false, outcome: 'other' };
}

// Best effort: declining only stops this user's ringing, so a failure changes nothing.
export async function declineInstantVideoRequest(practiceId, requestId) {
    try {
        const res = await callApi(`${base(practiceId)}requests/${encodeURIComponent(requestId)}/decline/`, 'POST', {}, false, quiet);
        return { ok: !!res?.ok };
    } catch { return { ok: false }; }
}
